---
title: Manage credentials
summary: Store your own third-party API keys, or use admin-managed credential sets shared across a team.
visibility: authenticated
audience: author-admin
section: How-to
order: 66
last_verified: 2026-10-07
owner: docs
---

# Manage credentials

Personas use third-party services — speech, avatars, payments — that need API keys. All
keys are encrypted before they're stored. There are two ways keys reach a persona.

## Your own keys (Your settings → API Keys)

Open **Your settings** from the user menu at the foot of the sidebar and go to the **API Keys**
tab. Each supported service has a field where you paste a key and **Save** it (or **Clear** it). Supported services include ElevenLabs,
Deepgram, OpenAI, Tavus, Anam, Azure Speech (which also needs a **Region**), the RAG
server, and Stripe.

If a service shows a banner saying the credential is provided by your administrator, it's
coming from a **credential set** (below) and is read-only for you — contact your
administrator to change it.

## Admin: the Integrations hub

Admins manage service keys at **Admin → Integrations**: one row per service
showing where it's configured (which credential set) and when the connection
was last verified. Click a row to see each field's effective value — including
adapter defaults — without exposing any stored secret.

Click **Edit** in the drawer to change a service's key or settings:

- The save button says exactly how many fields you're changing ("Update 2
  changed fields"), with a summary of each change above it, and stays disabled
  until something actually differs.
- Leaving the key field blank **keeps the existing key** — the form says so
  explicitly. Paste a new key to replace it; where a service has an automated
  test, a pasted key is checked immediately, before you save.
- **Clear** on a field removes the stored value so the service's default
  (shown in the field table) applies again.
- Saving a change resets the verification status — run **Test connection**
  afterwards to re-verify. The result is remembered and shown on the hub.

### Services with more than one secret

Some services need additional secrets beyond the API key. Stripe, for
example, has an optional **Webhook signing secret** (`whsec_...`, from
Stripe → Developers → Webhooks → your endpoint) used to verify incoming
payment notifications. Additional secrets are stored encrypted like the
API key and are never displayed after saving — the drawer shows only
whether each one is configured. When a webhook signing secret is
configured here, it takes precedence over one supplied via deployment
environment variables.

To configure a service for the first time, open its row and choose
**Configure**, picking which credential set the key is stored in.

The credential sets themselves (grouping, user assignment, delete) are managed
on the **Advanced: credential sets** page (`/admin/credential-sets`), linked
from the hub header. From there an admin can:

- **Create a set** with a name and description.
- **Manage Users** — assign the set to users. A user belongs to at most one set
  at a time; the set's keys then override that user's personal keys for those
  services.

Deleting a set removes its keys, and assigned users lose access to them.

## Credentials for a persona's tools

Separately, when a persona uses **tools** (on deployments with the advanced runtime), each
tool is wired to a credential set so the action can authenticate — see
[Personas, bundles, and workflows](/help/concepts/personas-bundles-workflows). That wiring
is configured on the persona's tools, not in your personal Settings.

## See also

- [Set up voice](/help/how-to/voice-setup) and [Set up a video avatar](/help/how-to/avatar-setup) — which keys they need.
